/**
 * useOnboardingNote — single-row CRUD for the "Anotações do onboarding" note
 * stored in user_notes (keyed by title). Used by FunnelStep and Perfil.
 *
 * Returns `notes` (string) + `setNotes` (debounced upsert/delete).
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "./useProfileId";

const ONBOARDING_NOTE_TITLE = "Anotações do onboarding";
const DEBOUNCE_MS = 800;

export interface UseOnboardingNoteResult {
  notes: string;
  setNotes: (next: string) => void;
  isLoaded: boolean;
}

export function useOnboardingNote(): UseOnboardingNoteResult {
  const { client, isReady: supabaseReady } = useSupabaseClient();
  const { profileId } = useProfileId();
  const qc = useQueryClient();

  const [notes, setNotesState] = useState("");
  const seededRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryKey = ["onboarding-note", profileId ?? "anon", supabaseReady] as const;

  const noteQuery = useQuery({
    queryKey,
    enabled: Boolean(profileId) && supabaseReady,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await client
        .from("user_notes")
        .select("id, content")
        .eq("user_id", profileId!)
        .eq("title", ONBOARDING_NOTE_TITLE)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  useEffect(() => {
    if (seededRef.current) return;
    if (noteQuery.data) {
      seededRef.current = true;
      setNotesState(noteQuery.data.content ?? "");
    } else if (noteQuery.isFetched) {
      seededRef.current = true;
    }
  }, [noteQuery.data, noteQuery.isFetched]);

  const setNotes = (next: string) => {
    setNotesState(next);
    if (!profileId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const trimmed = next.trim();
      if (noteQuery.data?.id) {
        if (trimmed.length === 0) {
          await client.from("user_notes").delete().eq("id", noteQuery.data.id);
        } else {
          await client
            .from("user_notes")
            .update({ content: trimmed })
            .eq("id", noteQuery.data.id);
        }
      } else if (trimmed.length > 0) {
        await client.from("user_notes").insert({
          user_id: profileId,
          title: ONBOARDING_NOTE_TITLE,
          content: trimmed,
        });
      }
      void qc.invalidateQueries({ queryKey });
    }, DEBOUNCE_MS);
  };

  return {
    notes,
    setNotes,
    isLoaded: seededRef.current,
  };
}
